import React from 'react';
import { Helmet } from 'react-helmet';

/**
 * Reusable SEO Component for NammaEventz
 * 
 * Automatically manages meta tags, Open Graph, and canonical URLs
 * Provides sensible defaults for the entire platform
 * 
 * @param {Object} props - SEO configuration
 * @param {string} props.title - Page title (will be formatted with suffix)
 * @param {string} props.description - Meta description
 * @param {string} props.keywords - Comma-separated keywords
 * @param {string} props.canonical - Canonical URL (defaults to current domain)
 * @param {string} props.ogTitle - Open Graph title (defaults to title)
 * @param {string} props.ogDescription - Open Graph description (defaults to description)
 * @param {string} props.ogImage - Open Graph image URL
 * @param {string} props.ogType - Open Graph type (default: website)
 * @returns {JSX.Element}
 */
const SEO = ({
  title = 'Home',
  description,
  keywords,
  canonical,
  ogTitle,
  ogDescription,
  ogImage = 'https://nammaeventz.com/og-image.jpg',
  ogType = 'website',
  children
}) => {
  // Default SEO values for NammaEventz
  const DEFAULT_TITLE_TEMPLATE = ' | NammaEventz – Best Event Management Platform in Tamil Nadu';
  const DEFAULT_DESCRIPTION =
    'NammaEventz is a trusted event management platform in Tamil Nadu helping you find verified vendors for weddings, birthdays, and corporate events. Plan events easily with reliable service providers.';
  const DEFAULT_KEYWORDS =
    'event management in tamil nadu,event planners near me,wedding event planners,birthday party organizers,corporate event management,event service providers,catering services for events,wedding decoration services,stage decoration,photography for events,dj services for events,event management website india,event vendors marketplace,tamil nadu event services,nammaeventz';

  // Construct page title
  const pageTitle = title === 'Home' 
    ? 'NammaEventz – Best Event Management Platform in Tamil Nadu'
    : `${title}${DEFAULT_TITLE_TEMPLATE}`;

  // Use provided values or defaults
  const metaDescription = description || DEFAULT_DESCRIPTION;
  const metaKeywords = keywords || DEFAULT_KEYWORDS;
  const canonicalUrl = canonical || 'https://nammaeventz.com/';
  const ogTitleValue = ogTitle || title;
  const ogDescriptionValue = ogDescription || metaDescription;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{pageTitle}</title>
      <meta name="title" content={pageTitle} />
      <meta name="description" content={metaDescription} />
      <meta name="keywords" content={metaKeywords} />
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      <meta name="theme-color" content="#1f2937" />
      
      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={ogTitleValue} />
      <meta property="og:description" content={ogDescriptionValue} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content="NammaEventz" />
      <meta property="og:locale" content="en_IN" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={ogTitleValue} />
      <meta name="twitter:description" content={ogDescriptionValue} />
      <meta name="twitter:image" content={ogImage} />

      {/* Additional SEO Meta Tags */}
      <meta name="language" content="English" />
      <meta name="author" content="NammaEventz" />
      <meta name="copyright" content="NammaEventz" />
      <meta name="robots" content="index, follow" />
      <meta name="revisit-after" content="7 days" />

      {/* Additional meta tags passed as children */}
      {children}
    </Helmet>
  );
};

export default SEO;
